<template>
  <div class="food-menu-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <div class="title-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="title-text">
            <h1>Food and Menu Management</h1>
            <p>Manage weekly food menus for breakfast, lunch, snacks, and dinner</p>
          </div>
        </div>
        <button class="back-btn" @click="goBack" aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back
        </button>
      </div>
    </div>

    <!-- Stats Section -->
    <div v-if="!isLoading" class="stats-section">
      <div class="stat-card">
        <div class="stat-icon total">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ getTotalMenus() }}</h3>
          <p>Total Menus</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ getDaysWithMenus() }}</h3>
          <p>Days Configured</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon meals">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <div class="stat-content">
          <h3>{{ getTotalMeals() }}</h3>
          <p>Total Meals</p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="content-area">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <span class="loading-text">Loading menu data...</span>
        </div>
      </div>

      <!-- Days Grid -->
      <div v-else class="days-grid">
        <div 
          v-for="day in daysOfWeek" 
          :key="day.key"
          class="day-card"
        >
          <div class="day-card-header">
            <div class="day-name-section">
              <h3 class="day-name">{{ day.label }}</h3>
              <span v-if="menuData[day.key] && hasMenuItems(menuData[day.key])" class="day-badge">
                Configured
              </span>
            </div>
            <div class="day-actions">
              <button 
                v-if="menuData[day.key] && hasMenuItems(menuData[day.key])"
                class="action-btn edit-btn" 
                @click="openAddMenuModal(day.key)"
                title="Edit menu"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button 
                v-if="menuData[day.key] && hasMenuItems(menuData[day.key])"
                class="action-btn delete-btn" 
                @click="promptDeleteMenu(day.key)"
                title="Delete menu"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!menuData[day.key] || !hasMenuItems(menuData[day.key])" class="day-empty-state">
            <div class="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 5h-2v6h2V7zm0 8h-2v2h2v-2z"/>
              </svg>
            </div>
            <p class="empty-text">No menu items added yet</p>
            <button class="add-button" @click="openAddMenuModal(day.key)">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Add Menu
            </button>
          </div>

          <!-- Menu Details -->
          <div v-else class="day-menu-details">
            <div class="meals-table">
              <div class="meals-table-header">
                <div class="table-col meal-col">Meal</div>
                <div class="table-col time-col">Serving Time</div>
                <div class="table-col food-col">Food Items</div>
                <div class="table-col count-col">Items</div>
              </div>
              <div class="meals-table-body">
                <template v-for="meal in ['breakfast', 'launch', 'snacks', 'dinner']" :key="meal">
                  <div 
                    v-if="menuData[day.key]?.[meal]?.food?.length > 0"
                    class="meal-row"
                    :class="`meal-row-${meal}`"
                  >
                    <div class="table-col meal-col">
                      <div class="meal-name-cell">
                        <div class="meal-icon" :class="`meal-icon-${meal}`">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                        </div>
                        <span class="meal-name-text">{{ capitalizeFirst(meal) }}</span>
                      </div>
                    </div>
                    <div class="table-col time-col">
                      <span class="time-display" v-if="menuData[day.key][meal]?.servingTime">
                        {{ menuData[day.key][meal].servingTime }}
                      </span>
                      <span v-else class="time-display empty">-</span>
                    </div>
                    <div class="table-col food-col">
                      <div class="food-items-row">
                        <span 
                          v-for="(food, index) in menuData[day.key][meal].food" 
                          :key="index"
                          class="food-item-chip"
                          :class="`food-chip-${meal}`"
                        >
                          {{ food }}
                        </span>
                      </div>
                    </div>
                    <div class="table-col count-col">
                      <span class="item-count">{{ menuData[day.key][meal].food.length }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Add/Edit Menu Modal -->
    <div 
      v-if="showModal" 
      class="modal-overlay" 
      @click.self="closeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Add Food Menu for {{ getDayLabel(selectedDay) }}</h2>
          <button class="close-btn" @click="closeModal" aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Breakfast Section -->
          <div class="meal-section">
            <h3 class="meal-section-title">Breakfast</h3>
            <div class="serving-time-group">
              <div class="time-input-wrapper">
                <label class="time-label">Start Time</label>
                <input 
                  type="time" 
                  v-model="form.breakfast.startTime"
                  class="time-input"
                />
              </div>
              <div class="time-input-wrapper">
                <label class="time-label">End Time</label>
                <input 
                  type="time" 
                  v-model="form.breakfast.endTime"
                  class="time-input"
                />
              </div>
            </div>
            <div class="food-input-wrapper">
              <div class="food-input-container">
                <input
                  v-model="breakfastInput"
                  @keydown.enter.prevent="addFoodItem('breakfast', breakfastInput)"
                  @keydown="handleKeydown($event, 'breakfast', breakfastInput)"
                  type="text"
                  placeholder="Add Food Menu"
                  class="food-input"
                />
                <button 
                  class="add-food-btn"
                  @click="addFoodItem('breakfast', breakfastInput)"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div v-if="form.breakfast.food.length > 0" class="food-tags">
                <span 
                  v-for="(item, index) in form.breakfast.food" 
                  :key="index"
                  class="food-tag"
                >
                  {{ item }}
                  <button 
                    class="tag-remove"
                    @click="removeFoodItem('breakfast', index)"
                    type="button"
                  >
                    ✕
                  </button>
                </span>
              </div>
            </div>
          </div>

          <!-- Lunch Section -->
          <div class="meal-section">
            <h3 class="meal-section-title">Lunch</h3>
            <div class="serving-time-group">
              <div class="time-input-wrapper">
                <label class="time-label">Start Time</label>
                <input 
                  type="time" 
                  v-model="form.launch.startTime"
                  class="time-input"
                />
              </div>
              <div class="time-input-wrapper">
                <label class="time-label">End Time</label>
                <input 
                  type="time" 
                  v-model="form.launch.endTime"
                  class="time-input"
                />
              </div>
            </div>
            <div class="food-input-wrapper">
              <div class="food-input-container">
                <input
                  v-model="lunchInput"
                  @keydown.enter.prevent="addFoodItem('launch', lunchInput)"
                  @keydown="handleKeydown($event, 'launch', lunchInput)"
                  type="text"
                  placeholder="Add Food Menu"
                  class="food-input"
                />
                <button 
                  class="add-food-btn"
                  @click="addFoodItem('launch', lunchInput)"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div v-if="form.launch.food.length > 0" class="food-tags">
                <span 
                  v-for="(item, index) in form.launch.food" 
                  :key="index"
                  class="food-tag"
                >
                  {{ item }}
                  <button 
                    class="tag-remove"
                    @click="removeFoodItem('launch', index)"
                    type="button"
                  >
                    ✕
                  </button>
                </span>
              </div>
            </div>
          </div>

          <!-- Snacks Section -->
          <div class="meal-section">
            <h3 class="meal-section-title">Snacks</h3>
            <div class="serving-time-group">
              <div class="time-input-wrapper">
                <label class="time-label">Start Time</label>
                <input 
                  type="time" 
                  v-model="form.snacks.startTime"
                  class="time-input"
                />
              </div>
              <div class="time-input-wrapper">
                <label class="time-label">End Time</label>
                <input 
                  type="time" 
                  v-model="form.snacks.endTime"
                  class="time-input"
                />
              </div>
            </div>
            <div class="food-input-wrapper">
              <div class="food-input-container">
                <input
                  v-model="snacksInput"
                  @keydown.enter.prevent="addFoodItem('snacks', snacksInput)"
                  @keydown="handleKeydown($event, 'snacks', snacksInput)"
                  type="text"
                  placeholder="Add Food Menu"
                  class="food-input"
                />
                <button 
                  class="add-food-btn"
                  @click="addFoodItem('snacks', snacksInput)"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div v-if="form.snacks.food.length > 0" class="food-tags">
                <span 
                  v-for="(item, index) in form.snacks.food" 
                  :key="index"
                  class="food-tag"
                >
                  {{ item }}
                  <button 
                    class="tag-remove"
                    @click="removeFoodItem('snacks', index)"
                    type="button"
                  >
                    ✕
                  </button>
                </span>
              </div>
            </div>
          </div>

          <!-- Dinner Section -->
          <div class="meal-section">
            <h3 class="meal-section-title">Dinner</h3>
            <div class="serving-time-group">
              <div class="time-input-wrapper">
                <label class="time-label">Start Time</label>
                <input 
                  type="time" 
                  v-model="form.dinner.startTime"
                  class="time-input"
                />
              </div>
              <div class="time-input-wrapper">
                <label class="time-label">End Time</label>
                <input 
                  type="time" 
                  v-model="form.dinner.endTime"
                  class="time-input"
                />
              </div>
            </div>
            <div class="food-input-wrapper">
              <div class="food-input-container">
                <input
                  v-model="dinnerInput"
                  @keydown.enter.prevent="addFoodItem('dinner', dinnerInput)"
                  @keydown="handleKeydown($event, 'dinner', dinnerInput)"
                  type="text"
                  placeholder="Add Food Menu"
                  class="food-input"
                />
                <button 
                  class="add-food-btn"
                  @click="addFoodItem('dinner', dinnerInput)"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <div v-if="form.dinner.food.length > 0" class="food-tags">
                <span 
                  v-for="(item, index) in form.dinner.food" 
                  :key="index"
                  class="food-tag"
                >
                  {{ item }}
                  <button 
                    class="tag-remove"
                    @click="removeFoodItem('dinner', index)"
                    type="button"
                  >
                    ✕
                  </button>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            class="save-button" 
            @click="saveMenu"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div 
      v-if="showDeleteDialog" 
      class="modal-overlay" 
      @click.self="cancelDelete"
    >
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header delete-header">
          <div class="modal-icon delete-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </div>
          <h3 class="modal-title">Delete Menu</h3>
          <p class="modal-description">Are you sure you want to delete the menu for {{ getDayLabel(dayToDelete) }}? This action cannot be undone.</p>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="cancelDelete">Cancel</button>
          <button class="btn-danger" @click="confirmDelete">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Delete Menu
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <transition-group name="toast" tag="div" class="toast-container">
      <div 
        v-for="toast in toasts" 
        :key="toast.id"
        :class="['toast', `toast-${toast.type}`]"
      >
        {{ toast.message }}
      </div>
    </transition-group>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';

export default {
  name: 'FoodMenu',
  props: {
    objectData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      daysOfWeek: [
        { key: 'sunday', label: 'Sunday' },
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' }
      ],
      menuData: {},
      showModal: false,
      selectedDay: '',
      isSubmitting: false,
      isLoading: false,
      breakfastInput: '',
      lunchInput: '',
      snacksInput: '',
      dinnerInput: '',
      form: {
        breakfast: {
          startTime: '09:00',
          endTime: '10:00',
          food: []
        },
        launch: {
          startTime: '',
          endTime: '',
          food: []
        },
        snacks: {
          startTime: '',
          endTime: '',
          food: []
        },
        dinner: {
          startTime: '',
          endTime: '',
          food: []
        }
      },
      toasts: [],
      showDeleteDialog: false,
      dayToDelete: ''
    };
  },
  mounted() {
    this.fetchMenuData();
  },
  methods: {
    async fetchMenuData() {
      this.isLoading = true;
      try {
        const objectId = this.$route.params.id;
        
        const response = await apiService.getFoodMenu(objectId);

        if (response.data?.data) {
          // Transform API response to our data structure
          const menus = Array.isArray(response.data.data) 
            ? response.data.data 
            : [response.data.data];
          
          menus.forEach(menu => {
            if (menu.day) {
              // API returns day in uppercase (SUNDAY), convert to lowercase for component
              const dayKey = menu.day.toLowerCase();
              // Store the full menu object including menuId if available
              this.menuData[dayKey] = menu;
            }
          });
        }
      } catch (error) {
        // Handle "no data" case - set empty array without showing error
        if (isNoDataError(error)) {
          this.menuItems = [];
          return;
        }
        if (isHmsSubscriptionInactiveError(error)) {
          return;
        }
        console.error('Failed to fetch menu data:', error);
        // Handle API errors with reason field for HTTP 400
        let message = null;
        if (error?.response?.data) {
          const errorData = error.response.data;
          const status = error.response.status;
          
          // For HTTP 400 errors, always use the reason field from API response
          if (status === 400) {
            if (errorData.reason) {
              message = errorData.reason;
            }
            // If no reason provided for 400 error, don't show toast (no generic messages)
          } else if (status !== 404) {
            // For other status codes (except 404), prioritize reason field
            if (errorData.reason) {
              message = errorData.reason;
            } else {
              message = 'Failed to load menu data';
            }
          }
        }
        
        // Only show toast if we have a message (genuine errors only)
        if (message) {
          this.showToast(message, 'error');
        }
      } finally {
        this.isLoading = false;
      }
    },


    hasMenuItems(dayMenu) {
      if (!dayMenu) return false;
      const meals = ['breakfast', 'launch', 'snacks', 'dinner'];
      return meals.some(meal => dayMenu[meal]?.food?.length > 0);
    },

    getMealItems(mealData) {
      if (!mealData?.food) return '';
      return mealData.food.join(', ');
    },

    capitalizeFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    getDayLabel(dayKey) {
      const day = this.daysOfWeek.find(d => d.key === dayKey);
      return day ? day.label : dayKey;
    },

    openAddMenuModal(dayKey) {
      this.selectedDay = dayKey;
      
      // Load existing data if available
      const existingMenu = this.menuData[dayKey];
      if (existingMenu) {
        this.form.breakfast = {
          startTime: this.parseServingTime(existingMenu.breakfast?.servingTime, 'start') || '09:00',
          endTime: this.parseServingTime(existingMenu.breakfast?.servingTime, 'end') || '10:00',
          food: existingMenu.breakfast?.food || []
        };
        this.form.launch = {
          startTime: this.parseServingTime(existingMenu.launch?.servingTime, 'start') || '',
          endTime: this.parseServingTime(existingMenu.launch?.servingTime, 'end') || '',
          food: existingMenu.launch?.food || []
        };
        this.form.snacks = {
          startTime: this.parseServingTime(existingMenu.snacks?.servingTime, 'start') || '',
          endTime: this.parseServingTime(existingMenu.snacks?.servingTime, 'end') || '',
          food: existingMenu.snacks?.food || []
        };
        this.form.dinner = {
          startTime: this.parseServingTime(existingMenu.dinner?.servingTime, 'start') || '',
          endTime: this.parseServingTime(existingMenu.dinner?.servingTime, 'end') || '',
          food: existingMenu.dinner?.food || []
        };
      } else {
        // Reset form
        this.form = {
          breakfast: {
            startTime: '09:00',
            endTime: '10:00',
            food: []
          },
          launch: {
            startTime: '',
            endTime: '',
            food: []
          },
          snacks: {
            startTime: '',
            endTime: '',
            food: []
          },
          dinner: {
            startTime: '',
            endTime: '',
            food: []
          }
        };
      }
      
      this.breakfastInput = '';
      this.lunchInput = '';
      this.snacksInput = '';
      this.dinnerInput = '';
      this.showModal = true;
      document.body.style.overflow = 'hidden';
    },

    parseServingTime(servingTime, type) {
      if (!servingTime) return '';
      
      // Parse format like "6am - 7am" or "09:00 AM - 10:00 AM"
      const parts = servingTime.split('-').map(s => s.trim());
      if (parts.length !== 2) return '';
      
      const timeStr = type === 'start' ? parts[0] : parts[1];
      
      // Convert "6am" to "06:00" or "09:00 AM" to "09:00"
      if (timeStr.includes('am') || timeStr.includes('pm')) {
        const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/i);
        if (match) {
          let hours = parseInt(match[1]);
          const minutes = match[2] ? parseInt(match[2]) : 0;
          const period = match[3].toLowerCase();
          
          if (period === 'pm' && hours !== 12) hours += 12;
          if (period === 'am' && hours === 12) hours = 0;
          
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
      }
      
      return timeStr;
    },

    formatServingTime(startTime, endTime) {
      if (!startTime || !endTime) return '';
      
      // Convert "09:00" to "9am" format
      const formatTime = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        let period = 'am';
        let displayHours = hours;
        
        if (hours >= 12) {
          period = 'pm';
          if (hours > 12) displayHours = hours - 12;
        }
        if (hours === 0) displayHours = 12;
        
        const mins = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
        return `${displayHours}${mins}${period}`;
      };
      
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    },

    closeModal() {
      this.showModal = false;
      document.body.style.overflow = 'auto';
    },

    handleKeydown(event, mealType, inputValue) {
      // Handle comma key (keyCode 188 or key ',')
      if (event.key === ',' || event.keyCode === 188) {
        event.preventDefault();
        this.addFoodItem(mealType, inputValue);
      }
    },

    addFoodItem(mealType, inputValue) {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      
      if (!this.form[mealType].food.includes(trimmed)) {
        this.form[mealType].food.push(trimmed);
      }
      
      // Clear input
      if (mealType === 'breakfast') this.breakfastInput = '';
      else if (mealType === 'launch') this.lunchInput = '';
      else if (mealType === 'snacks') this.snacksInput = '';
      else if (mealType === 'dinner') this.dinnerInput = '';
    },

    removeFoodItem(mealType, index) {
      this.form[mealType].food.splice(index, 1);
    },

    async saveMenu() {
      try {
        this.isSubmitting = true;
        const objectId = this.$route.params.id;
        
        // Build payload - API expects day in uppercase
        const payload = {
          day: this.selectedDay.toUpperCase(),
          breakfast: {
            servingTime: this.formatServingTime(
              this.form.breakfast.startTime,
              this.form.breakfast.endTime
            ),
            food: this.form.breakfast.food
          },
          launch: {
            servingTime: this.formatServingTime(
              this.form.launch.startTime,
              this.form.launch.endTime
            ),
            food: this.form.launch.food
          },
          snacks: {
            servingTime: this.formatServingTime(
              this.form.snacks.startTime,
              this.form.snacks.endTime
            ),
            food: this.form.snacks.food
          },
          dinner: {
            servingTime: this.formatServingTime(
              this.form.dinner.startTime,
              this.form.dinner.endTime
            ),
            food: this.form.dinner.food
          }
        };

        // Check if menu already exists (for update)
        const existingMenu = this.menuData[this.selectedDay];
        const menuId = existingMenu?.menuId || existingMenu?.id || existingMenu?._id;
        
        // Use PUT for update, POST for create
        if (menuId) {
          // Update existing menu - send full payload
          await apiService.updateFoodMenu(menuId, objectId, payload);
        } else {
          // Create new menu
          await apiService.createFoodMenu(objectId, payload);
        }

        this.showToast(menuId ? 'Menu updated successfully!' : 'Menu saved successfully!', 'success');
        this.closeModal();
        await this.fetchMenuData();
      } catch (error) {
        console.error('Failed to save menu:', error);
        const errorMessage = error.response?.data?.reason || 
                           error.response?.data?.message || 
                           'Failed to save menu. Please try again.';
        this.showToast(errorMessage, 'error');
      } finally {
        this.isSubmitting = false;
      }
    },

    showToast(message, type = 'error', duration = 5000) {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, message, type });
      
      if (duration > 0) {
        setTimeout(() => {
          this.removeToast(id);
        }, duration);
      }
    },

    removeToast(id) {
      this.toasts = this.toasts.filter(toast => toast.id !== id);
    },

    goBack() {
      this.$router.go(-1);
    },

    getTotalMenus() {
      return Object.keys(this.menuData).filter(key => this.hasMenuItems(this.menuData[key])).length;
    },

    getDaysWithMenus() {
      return Object.keys(this.menuData).filter(key => this.hasMenuItems(this.menuData[key])).length;
    },

    getTotalMeals() {
      let total = 0;
      Object.values(this.menuData).forEach(menu => {
        if (menu) {
          ['breakfast', 'launch', 'snacks', 'dinner'].forEach(meal => {
            if (menu[meal]?.food?.length > 0) {
              total++;
            }
          });
        }
      });
      return total;
    },

    promptDeleteMenu(dayKey) {
      this.dayToDelete = dayKey;
      this.showDeleteDialog = true;
      document.body.style.overflow = 'hidden';
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.dayToDelete = '';
      document.body.style.overflow = 'auto';
    },

    async confirmDelete() {
      try {
        const objectId = this.$route.params.id;
        const menuToDelete = this.menuData[this.dayToDelete];
        
        // Get menuId from the menu data
        const menuId = menuToDelete?.menuId || menuToDelete?.id || menuToDelete?._id;
        
        if (!menuId) {
          // If no menuId, just remove from local data (menu might not exist on server)
          delete this.menuData[this.dayToDelete];
          this.showToast('Menu removed successfully!', 'success');
          this.cancelDelete();
          return;
        }

        // Use DELETE API
        await apiService.deleteFoodMenu(menuId, objectId);

        // Remove from local data
        delete this.menuData[this.dayToDelete];
        this.showToast('Menu deleted successfully!', 'success');
        this.cancelDelete();
      } catch (error) {
        console.error('Failed to delete menu:', error);
        const errorMessage = error.response?.data?.reason || 
                           error.response?.data?.message || 
                           'Failed to delete menu. Please try again.';
        this.showToast(errorMessage, 'error');
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = 'auto';
  }
};
</script>

<style scoped>
.food-menu-container {
  padding: 0;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header Section */
.header-section {
  background: white;
  border-radius: 0;
  padding: 16px 24px;
  margin-bottom: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: none;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 100;
  flex-shrink: 0;
  margin-top: 20px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  max-width: 1400px;
  margin: 0 auto;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.title-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
  color: #ffffff;
  flex-shrink: 0;
}

.title-icon svg {
  width: 20px;
  height: 20px;
}

.title-text {
  text-align: left;
}

.title-text h1 {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.025em;
  text-align: left;
}

.title-text p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  font-weight: 400;
  text-align: left;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #5b2e8e;
  color: #5b2e8e;
}

.back-btn svg {
  width: 20px;
  height: 20px;
}

/* Stats Section */
.stats-section {
  background: white;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid #e2e8f0;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 200px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.total {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  color: #ffffff;
}

.stat-icon.active {
  background: #ecfdf5;
  color: #065f46;
}

.stat-icon.meals {
  background: #eff6ff;
  color: #1e40af;
}

.stat-icon svg {
  width: 24px;
  height: 24px;
}

.stat-content {
  flex: 1;
  text-align: left;
}

.stat-content h3 {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  line-height: 1;
  text-align: left;
}

.stat-content p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  font-weight: 500;
  text-align: left;
}

/* Content Area */
.content-area {
  padding: 0 24px 24px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Loading State */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner-ring {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #5b2e8e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Days Grid */
.days-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.day-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 0 0 auto;
}

.day-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  transform: scaleX(0);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.day-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.day-card:hover::before {
  transform: scaleX(1);
}

.day-card-header {
  padding: 1.5rem 1.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.day-name-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  flex-wrap: wrap;
}

.day-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.02em;
}

.day-badge {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: #ffffff;
  padding: 0.375rem 0.875rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(91, 46, 142, 0.3);
  white-space: nowrap;
}

.day-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.edit-btn {
  color: #5b2e8e;
}

.edit-btn:hover {
  background: #e0f2fe;
  border-color: #bae6fd;
  color: #0369a1;
  transform: translateY(-1px);
}

.delete-btn {
  color: #dc2626;
}

.delete-btn:hover {
  background: #fee2e2;
  border-color: #fecaca;
  color: #dc2626;
  transform: translateY(-1px);
}

/* Day Empty State */
.day-empty-state {
  padding: 3rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 250px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
}

.empty-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.25rem;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.empty-icon svg {
  width: 36px;
  height: 36px;
}

.empty-text {
  color: #64748b;
  font-size: 15px;
  margin: 0 0 1.5rem 0;
  font-weight: 500;
  line-height: 1.5;
}

/* Day Menu Details */
.day-menu-details {
  padding: 0 1.5rem 1.5rem;
}

/* Meals Table Layout */
.meals-table {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.meals-table-header {
  display: grid;
  grid-template-columns: 140px 140px 1fr 80px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.table-col {
  display: flex;
  align-items: center;
}

.meals-table-body {
  display: flex;
  flex-direction: column;
}

.meal-row {
  display: grid;
  grid-template-columns: 140px 140px 1fr 80px;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  align-items: center;
}

.meal-row:last-child {
  border-bottom: none;
}

.meal-row:hover {
  background: #f8fafc;
}

/* Meal Name Cell */
.meal-name-cell {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.meal-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

/* Food-themed colors for meals */
.meal-icon-breakfast {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #ffffff;
}

.meal-icon-launch {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
}

.meal-icon-snacks {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
}

.meal-icon-dinner {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #ffffff;
}

.meal-name-text {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  text-transform: capitalize;
}

/* Time Display */
.time-col {
  font-size: 0.875rem;
  color: #64748b;
}

.time-display {
  font-weight: 500;
}

.time-display.empty {
  color: #cbd5e1;
}

/* Food Items Row */
.food-col {
  min-width: 0;
  overflow: hidden;
}

.food-items-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  max-width: 100%;
  /* Ensure proper wrapping with many items */
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.food-item-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 16px;
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  /* Handle long food names gracefully */
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Food-themed colors for chips */
.food-chip-breakfast {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #fcd34d;
}

.food-chip-launch {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.food-chip-snacks {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.food-chip-dinner {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border: 1px solid #fca5a5;
}

.food-item-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* Item Count */
.count-col {
  justify-content: flex-end;
}

.item-count {
  background: #f1f5f9;
  color: #475569;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
}

.food-item-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.35);
}

/* Ensure meal rows don't break with many items */
.meal-row {
  min-height: fit-content;
  align-items: flex-start;
}

.meal-row .food-col {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

/* Handle many food items gracefully - ensure they wrap properly */
@media (min-width: 768px) {
  .food-items-row {
    max-width: 100%;
  }
}

.add-button {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 6px rgba(91, 46, 142, 0.2);
}

.add-button:hover {
  background: linear-gradient(135deg, #4a2373 0%, #5b2e8e 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.add-button svg {
  width: 16px;
  height: 16px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.meal-section {
  margin-bottom: 32px;
}

.meal-section:last-child {
  margin-bottom: 0;
}

.meal-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 16px 0;
  text-align: left;
}

.serving-time-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.time-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.time-label {
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  text-align: left;
  display: block;
}

.time-input {
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  width: 100%;
}

.time-input:focus {
  outline: none;
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.food-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.food-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 4px 4px 12px;
  transition: all 0.2s ease;
}

.food-input-container:focus-within {
  border-color: #5b2e8e;
  box-shadow: 0 0 0 3px rgba(91, 46, 142, 0.1);
}

.food-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 0;
  font-size: 14px;
  color: #1a202c;
}

.food-input::placeholder {
  color: #a0aec0;
}

.add-food-btn {
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.add-food-btn:hover {
  background: linear-gradient(135deg, #4a2373 0%, #5b2e8e 100%);
  transform: scale(1.05);
}

.food-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.food-tag {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
}

.tag-remove {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  padding: 0;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.tag-remove:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.save-button {
  padding: 12px 32px;
  background: linear-gradient(135deg, #5b2e8e 0%, #7c4dbd 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
}

.save-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #4a2373 0%, #5b2e8e 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(91, 46, 142, 0.3);
}

.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Delete Modal Styles */
.delete-modal {
  max-width: 480px;
  width: 100%;
}

.delete-header {
  flex-direction: column;
  text-align: center;
  padding: 32px 24px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  flex-shrink: 0;
}

.delete-icon {
  background: #fef2f2;
  color: #dc2626;
}

.delete-header .modal-title {
  color: #1e293b;
  margin-bottom: 8px;
}

.modal-description {
  color: #64748b;
  font-size: 14px;
  margin: 0;
  line-height: 1.6;
}

.modal-actions {
  padding: 20px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  background: white;
}

.btn-secondary {
  padding: 10px 20px;
  background: #f1f5f9;
  color: #475569;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #e2e8f0;
  color: #334155;
}

.btn-danger {
  padding: 10px 20px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-danger:hover {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}


/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toast {
  padding: 14px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  animation: slideInRight 0.3s ease;
}

.toast-success {
  background: #48bb78;
  color: white;
}

.toast-error {
  background: #f56565;
  color: white;
}

.toast-warning {
  background: #ed8936;
  color: white;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-content {
    flex-wrap: wrap;
  }
  
  .title-section {
    flex: 1;
    min-width: 0;
  }
  
  .days-grid {
    gap: 1.25rem;
  }
  
  .meals-table-header,
  .meal-row {
    grid-template-columns: 130px 130px 1fr 70px;
    gap: 0.875rem;
  }
  
  .stats-section {
    gap: 16px;
  }
  
  .stat-card {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .content-area {
    padding: 0 1rem 1.5rem;
  }
  
  .header-section {
    padding: 16px;
  }
  
  .header-content {
    flex-direction: row;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }
  
  .title-section {
    flex: 1;
    min-width: 0;
    gap: 12px;
  }
  
  .title-icon {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }
  
  .title-text {
    min-width: 0;
    flex: 1;
  }
  
  .title-text h1 {
    font-size: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .title-text p {
    display: none;
  }
  
  .back-btn {
    padding: 10px 16px;
    font-size: 14px;
    flex-shrink: 0;
  }
  
  .back-btn svg {
    width: 18px;
    height: 18px;
  }
  
  .stats-section {
    padding: 16px;
    gap: 12px;
  }
  
  .stat-card {
    min-width: calc(50% - 6px);
    padding: 16px;
  }
  
  .stat-content h3 {
    font-size: 20px;
  }
  
  .days-grid {
    gap: 1rem;
  }
  
  .day-card-header {
    padding: 1.25rem 1.25rem 0.875rem;
  }
  
  .day-empty-state {
    padding: 2.5rem 1.25rem;
    min-height: 220px;
  }
  
  .empty-icon {
    width: 64px;
    height: 64px;
  }
  
  .empty-icon svg {
    width: 32px;
    height: 32px;
  }
  
  .meals-table {
    overflow-x: auto;
  }
  
  .meals-table-header,
  .meal-row {
    grid-template-columns: 100px 100px 1fr 50px;
    gap: 0.625rem;
    padding: 0.875rem 1rem;
    min-width: 600px;
  }
  
  .meals-table-header {
    font-size: 0.75rem;
  }
  
  .meal-name-text {
    font-size: 0.875rem;
  }
  
  .meal-icon {
    width: 28px;
    height: 28px;
  }
  
  .meal-icon svg {
    width: 16px;
    height: 16px;
  }
  
  .food-item-chip {
    font-size: 0.75rem;
    padding: 0.3125rem 0.625rem;
  }
  
  .item-count {
    font-size: 0.75rem;
    padding: 0.3125rem 0.625rem;
    min-width: 35px;
  }
  
  .time-display {
    font-size: 0.8125rem;
  }
  
  .modal-content {
    max-width: 100%;
    max-height: 95vh;
    margin: 10px;
  }

  .serving-time-group {
    grid-template-columns: 1fr;
  }

  .toast-container {
    right: 10px;
    left: 10px;
  }

  .toast {
    min-width: auto;
  }

  .action-btn {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 480px) {
  .header-section {
    padding: 12px;
    margin-top: 10px;
  }
  
  .header-content {
    gap: 8px;
  }
  
  .title-icon {
    width: 36px;
    height: 36px;
  }
  
  .title-icon svg {
    width: 20px;
    height: 20px;
  }
  
  .title-text h1 {
    font-size: 18px;
  }
  
  .back-btn {
    padding: 8px 12px;
    font-size: 12px;
    gap: 6px;
  }
  
  .back-btn svg {
    width: 16px;
    height: 16px;
  }
  
  .stats-section {
    padding: 12px;
    gap: 8px;
  }
  
  .stat-card {
    min-width: 100%;
    padding: 12px;
  }
  
  .stat-icon {
    width: 40px;
    height: 40px;
  }
  
  .stat-icon svg {
    width: 20px;
    height: 20px;
  }
  
  .stat-content h3 {
    font-size: 18px;
  }
  
  .stat-content p {
    font-size: 12px;
  }
  
  .days-grid {
    gap: 0.75rem;
  }

  .day-card {
    border-radius: 16px;
    min-width: 320px;
  }
  
  .day-card-header {
    padding: 1.25rem 1.25rem 0.75rem;
  }
  
  .day-menu-details {
    padding: 0 1rem 1rem;
  }
  
  .meals-table {
    border-radius: 8px;
  }
  
  .meals-table-header,
  .meal-row {
    grid-template-columns: 80px 90px 1fr 45px;
    gap: 0.5rem;
    padding: 0.75rem 0.875rem;
    min-width: 500px;
  }
  
  .meals-table-header {
    font-size: 0.6875rem;
    padding: 0.75rem 0.875rem;
  }
  
  .meal-name-cell {
    gap: 0.5rem;
  }
  
  .meal-icon {
    width: 24px;
    height: 24px;
  }
  
  .meal-icon svg {
    width: 14px;
    height: 14px;
  }
  
  .meal-name-text {
    font-size: 0.8125rem;
  }
  
  .time-display {
    font-size: 0.75rem;
  }
  
  .food-item-chip {
    font-size: 0.6875rem;
    padding: 0.25rem 0.5rem;
  }
  
  .food-items-row {
    gap: 0.375rem;
  }
  
  .item-count {
    font-size: 0.6875rem;
    padding: 0.25rem 0.5rem;
    min-width: 30px;
  }
}
</style>

